/**
 * Korean message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: 해요체 (polite informal, locked in
 * l10n/locales.md) — the friendly modern-app voice (~요/~하세요).
 *
 * Platform terms follow Korean UI conventions (설정, 페어링 for
 * pairing, 미러링 for mirroring, 삭제 for dismiss, 대화 for
 * conversation). "Glance" is the untranslated feature brand name
 * (glossary D1); "클립" renders the D2 loanword in Hangul.
 *
 * Plural notes: Korean is an `other`-only CLDR locale — counter words
 * (개, 대, 건, 자) carry the number; every `selectPlural("ko", …)`
 * supplies only an `other` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "연결됨",
    statusReconnecting: "다시 연결하는 중…",
    statusDisconnected: "연결 끊김",
    openSettings: "설정 열기",
    pairedFallbackDevice: "내 휴대전화",
    publicModeToggleEnable: "알림 내용 숨기기",
    publicModeToggleDisable: "알림 내용 표시",
  },

  notifications: {
    replyAriaLabel: "답장",
    replyPlaceholder: "답장…",
    replySend: "보내기",
    sent: "보냈어요",
    failedToSend: "보내지 못했어요",
    unread: "읽지 않음",
    dismiss: "삭제",
    notificationImageAlt: "알림 이미지",
  },

  conversations: {
    replyAriaLabel: "답장",
    replyPlaceholder: "답장…",
    replySend: "보내기",
    sent: "보냈어요",
    failedToSend: "보내지 못했어요",
    dismiss: "대화 삭제",
    messagesListLabel: "이 대화의 메시지",
    deviceOwner: "나",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "읽지 않은 메시지 없음"
        : `읽지 않은 메시지 ${count}${selectPlural("ko", count, { other: "개" })}`,
  },

  list: {
    today: "오늘",
    yesterday: "어제",
    searchNotifications: "알림 검색",
    clearSearch: "검색어 지우기",
    clearAll: "모두 삭제",
    markAllRead: "모두 읽음으로 표시",
    groupCheckboxLabel: "그룹으로 묶기",
    noNotificationsTitle: "아직 알림이 없어요",
    noNotificationsDesc: "휴대전화에서 미러링된 새 메시지가 여기에 표시돼요.",
    noMatchesTitle: "일치하는 항목 없음",
    notificationsList: "알림",
    privacyAppList: "앱별 알림",
    clear: "삭제",
    clearMatches: (count: number): string =>
      `일치 항목 ${count}${selectPlural("ko", count, { other: "개" })} 삭제`,
    noMatchesDesc: (query: string): string => `"${query}"과(와) 일치하는 알림이 없어요.`,
    appNotificationsGroup: (appName: string): string => `${appName} 알림`,
    dismissAllFrom: (appName: string): string => `${appName}의 알림 모두 삭제`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const unit =
        kind === "messages"
          ? selectPlural("ko", count, { other: "개" })
          : kind === "calls"
            ? selectPlural("ko", count, { other: "건" })
            : selectPlural("ko", count, { other: "개" });
      const noun = kind === "messages" ? "메시지" : kind === "calls" ? "전화" : "알림";
      return `${noun} ${count}${unit}`;
    },
    kbdDismiss: "삭제",
    kbdNavigate: "이동",
    kbdOrSeparator: "또는",
  },

  settings: {
    back: "알림으로 돌아가기",
    settings: "설정",
    appearance: "모양",
    theme: "테마",
    themeAuto: "자동",
    themeLight: "라이트",
    themeDark: "다크",
    language: "언어",
    languageSystemDefault: "시스템 기본값",
    notificationsSection: "알림",
    osNotifications: "OS 알림",
    callNotifications: "전화 알림",
    privacySection: "개인정보",
    analyticsToggle: "익명 사용 통계",
    analyticsToggleDescription: "횟수와 마일스톤만 — 알림 내용은 절대 포함하지 않아요.",
    connection: "연결",
    status: "상태",
    statusConnected: "연결됨",
    statusReconnecting: "다시 연결하는 중…",
    statusDisconnected: "연결 끊김",
    disconnectFromPhone: "휴대전화 연결 끊기",
    disconnectConfirm: "정말 끊을까요? 기기의 페어링이 해제돼요.",
    yesDisconnect: "네, 연결을 끊을게요",
    cancel: "취소",
    aboutAndSupport: "정보 및 지원",
    version: "버전",
    encryption: "암호화",
    encryptionValue: "E2E (NaCl)",
    needHelp: "도움이 필요하세요? __EMAIL__(으)로 메일을 보내세요",
    disconnectFailed: "연결을 끊지 못했어요 — 다시 시도해 주세요",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `연결은 끊었지만 로컬 정리에 실패했어요: ${errorMsg}. 페이지를 새로고침해 주세요.`,
    disconnectLocalCleanupFailed:
      "연결은 끊었지만 로컬 정리에 실패했어요. 페이지를 새로고침해 주세요.",
  },

  pairing: {
    enterCodeInstruction: "이 코드를 Reflecto 앱에 입력하세요",
    pairedSuccessfully: "페어링이 완료됐어요!",
    pairingFailed: "페어링에 실패했어요",
    requestingPairingCode: "페어링 코드를 요청하는 중…",
    codeExpired: "코드가 만료됐어요",
    tryAgain: "다시 시도",
    failedToStartPairing: "페어링을 시작하지 못했어요",
    needAppText: "Android 앱이 필요하세요? __EMAIL__(으)로 메일을 보내 액세스를 요청하세요.",
  },

  dnd: {
    title: "방해 금지",
    description: "이 기기의 알림을 1시간 동안 일시중지해요.",
    snooze1h: "1시간 일시중지",
    snoozeActiveUntil: (formattedTime: string): string => `${formattedTime}까지 적용`,
    cancelSnooze: "취소",
  },

  connectors: {
    title: "연결된 서비스",
    emptyBody:
      "아직 연결된 서비스가 없어요. ChatGPT나 Claude의 커넥터 설정에서 연결한 다음, 휴대전화에서 코드를 승인하세요.",
    scopeSend: "알림을 보낼 수 있어요",
    added: (date: string): string => `추가됨 ${date}`,
    lastUsed: (relative: string): string => `마지막 사용 ${relative}`,
    neverUsed: "사용한 적 없음",
    revoke: "연결 해제",
    revokeConfirm: (label: string): string =>
      `${label}의 연결을 해제할까요? 즉시 접근할 수 없게 돼요.`,
    revokeAction: "연결 해제",
    revokeConfirmLabel: (label: string): string => `${label} 연결 해제`,
    cancel: "취소",
    loadError: "연결된 서비스를 불러오지 못했어요. 다시 시도해 주세요.",
    revokeError: "서비스 연결을 해제하지 못했어요. 다시 시도해 주세요.",
    retry: "다시 시도",
  },

  feedback: {
    title: "의견 보내기",
    close: "닫기",
    messageLabel: "메시지",
    messagePlaceholder: "버그를 찾았거나, 아이디어가 있거나, 뭔가 이상한가요? 들려주세요.",
    categoryLabel: "분류",
    categoryBug: "버그",
    categoryIdea: "아이디어",
    categoryOther: "기타",
    emailLabel: "이메일 (선택 사항, 답변이 필요할 때만)",
    emailPlaceholder: "you@example.com",
    emailHelp: "어떤 목록에도 추가하지 않아요.",
    disclosure:
      "보내주신 메시지는 Reflecto 서버로 전송되어 내부 이슈 트래커에 기록돼요. 리포트를 연결하기 위해 앱 버전과 페어링할 때 부여된 기기 ID를 함께 첨부해요. 알림, 연락처, 그 밖의 개인 데이터는 볼 수 없어요. 이메일은 선택 사항이며 답변이 필요할 때만 적어 주세요. 어떤 목록에도 추가하지 않아요.",
    submit: "의견 보내기",
    sending: "보내는 중…",
    success: "감사해요 — 보통 24시간 안에 사람이 직접 읽어요",
    error: "보내지 못했어요. 다시 시도해 주세요.",
    done: "완료",
    sendFeedback: "의견 보내기",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "닫기",
    aiSummaryTag: "AI 요약",
    errorAi: "지금은 요약을 만들지 못했어요.",
    retryCta: "다시 시도",
    downloadHeading: "AI 요약 받아보기",
    downloadBody:
      "기기에서 바로 동작하는 AI 요약으로 놓친 알림을 따라잡으세요. 전부 내 기기 안에서만 실행돼요.",
    downloadCta: "모델 다운로드",
    downloadPrivacyNote: "다운로드는 한 번뿐이에요. 서버는 알림을 절대 볼 수 없어요.",
    downloadingHeading: "모델 다운로드 중…",
    caughtUp: "모두 확인했어요 — 지금은 챙길 일이 없어요.",
    needsYou: "확인 필요",
    quiet: "그 외",
    scanning: (pending: number): string => `${pending}개 요약 중…`,
    jump: "알림 열기",
    affordanceReply: "답장",
    affordanceOpen: "열기",
    affordanceCopyOtp: "코드 복사",
    buttonLabel: "Glance",
  },

  update: {
    title: "업데이트가 있어요",
    description: "새 버전의 Reflecto가 준비됐어요. 새로고침해서 업데이트하세요.",
    reload: "새로고침",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `이 기기의 이름을 "${name}"(으)로 정했어요.`,
    cardLooksGood: "좋아요",
    cardEdit: "수정",
    cardAriaLabel: "기기 이름 확인",
    renameTitle: "이 기기 이름 변경",
    renameLabel: "기기 이름",
    renameSave: "저장",
    renameCancel: "취소",
    renameFailed: "이름을 변경하지 못했어요. 다시 시도해 주세요.",
  },

  collision: {
    title: (name: string): string => `이미 "${name}"이(가) 있어요`,
    subtitle: "이 기기를 뭐라고 부를까요?",
    chipWork: (name: string): string => `직장 ${name}`,
    chipHome: (name: string): string => `집 ${name}`,
    chipPersonal: (name: string): string => `개인 ${name}`,
    customLabel: "직접 입력",
    save: "저장",
    useAnyway: "원래 이름 그대로 사용",
    saveFailed: "새 이름을 저장하지 못했어요. 다시 시도해 주세요.",
    ariaLabel: "중복된 기기 이름 해결",
  },

  clipboard: {
    title: "클립보드",
    empty: "다른 기기의 클립이 여기에 표시돼요. 탭해서 복사하세요.",
    from: (label: string): string => `${label}에서`,
    copy: "클립보드에 복사",
    copied: "복사했어요",
    reveal: "표시",
    hide: "숨기기",
    sensitiveChip: "민감한 내용",
    cloudChip: "내 API 토큰 경유",
    maskedHint: '내용이 숨겨져 있어요 — "표시"를 눌러 확인하세요',
    sendPlaceholder: "보낼 텍스트 붙여넣기…",
    sendCta: "보내기",
    sendSensitiveToggle: "민감한 내용으로 표시",
    sendSuccess: (n: number): string =>
      `기기 ${n}${selectPlural("ko", n, { other: "대" })}로 보냈어요.`,
    sendPartial: (n: number, total: number): string =>
      `기기 ${total}${selectPlural("ko", total, { other: "대" })} 중 ${n}대로 보냈어요.`,
    sendNoDevices: "보낼 수 있는 페어링된 기기가 없어요.",
    sendFailed: "보내지 못했어요. 다시 시도해 주세요.",
    sendEmpty: "보낼 내용이 없어요.",
    dockExpand: "클립 기록 표시",
    dockCollapse: "접기",
    dockSendTitle: "내 기기로 보내기",
    dockFromClipboard: (chars: number): string =>
      `클립보드에서 · ${chars}${selectPlural("ko", chars, { other: "자" })}`,
    dockSend: (n: number): string => `${n}대로 보내기`,
    dockCancel: "취소",
    dockRecentHeading: "최근",
    dockEmptyClipboard: "클립보드가 비어 있어요.",
    shareTitle: "내 기기로 보내기",
    shareReview: "보낼 내용을 확인하세요. 페어링된 기기로 종단 간 암호화되어 전송돼요.",
    shareSend: "보내기",
    shareCancel: "취소",
    shareNotPaired: "공유한 텍스트를 보내려면 먼저 기기를 페어링하세요.",
    shareOpenApp: "Reflecto 열기",
  },
  install: {
    title: "Reflecto 설치",
    body: "홈 화면에 추가하면 더 빠르게 열 수 있어요.",
    cta: "설치",
    dismissAria: "설치 안내 닫기",
  },
  common: {
    loading: "로드 중",
  },
  errors: {
    notPaired: "페어링되지 않음",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os}의 ${browser}`,
    webFallback: "웹 브라우저",
  },
  palette: {
    placeholder: "명령어 입력…",
    noResults: "일치하는 명령 없음",
    ariaLabel: "명령어 팔레트",
    clearAll: "모든 알림 삭제",
    glance: "Glance 요약",
    cancelSnooze: "일시중지 취소",
    copyClip: "마지막으로 받은 클립 복사",
  },
} satisfies MessagesShape;
